import 'twin.macro';

import { useSessionStorage } from '@cheqroom/hooks';
import { ArrowLeft } from '@cheqroom/icons';
import { Avatar, Banner, Button, Heading, IconButton, Stack, Text, TextStyle } from '@cheqroom/ui';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { handlePostAuthentication } from '../../api/authenticate.api';
import { Workspace } from '../../api/discovery.api';
import { useRecentWorkspaces } from '../../api/find-workspaces.api';
import { Step } from '../../App';
import { StyledCard } from '../../App.styles';
import { requireEnv } from '../../config/env';
import { useRedirectUri } from '../../hooks/use-redirect-uri.hook';
import { type AuthTokens, getPythonErrorKey, type PythonErrorLike, UsernamePasswordForm } from '../../public-api/auth';

interface PasswordStepLocationState {
	state?: {
		email?: string;
		success?: string;
	};
}

export type PasswordLoginFormProps = {
	email?: string;
	workspace?: Pick<Workspace, 'name' | 'logo'>;
	onGoBackClick: () => void;
	canChangeEmail: boolean;
};

/**
 * Password (and optional email) login form for a chosen workspace.
 * Shows workspace avatar, success/error banners, and delegates to UsernamePasswordForm.
 */
const PasswordLoginForm: FC<PasswordLoginFormProps> = ({ email, workspace, onGoBackClick, canChangeEmail }) => {
	const { t } = useTranslation('login');
	const { workspaceId } = useParams<{ workspaceId: string }>();
	const { state } = useLocation() as PasswordStepLocationState;
	const navigateTo = useNavigate();

	const [isMobileApp] = useSessionStorage<boolean>('mobile');
	const [redirectUri, clearRedirectUri] = useRedirectUri();
	const { add: addRecentWorkspace } = useRecentWorkspaces();
	const [postAuthError, setPostAuthError] = useState<string | null>(null);

	const onSuccess = useCallback(
		(tokens: AuthTokens) => {
			setPostAuthError(null);
			void handlePostAuthentication({
				accessToken: tokens.access_token,
				refreshToken: tokens.refresh_token,
				isMobileApp,
				redirectUri,
				navigateTo,
				addRecentWorkspace,
				clearStorage: clearRedirectUri,
			}).catch((err) => {
				setPostAuthError(t('password_step.errors.uncaught'));
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line no-console
					console.error('Post-authentication failed:', err);
				}
			});
		},
		[isMobileApp, redirectUri, navigateTo, addRecentWorkspace, clearRedirectUri, t]
	);

	const defaultEmail = state?.email ?? email ?? '';
	const forgotPasswordHref =
		Step.FORGOT_PASSWORD + (defaultEmail ? `?email=${encodeURIComponent(defaultEmail)}` : '');

	const getErrorMessage = useCallback(
		(err: PythonErrorLike | null) => t('password_step.errors.' + getPythonErrorKey(err)),
		[t]
	);

	const forgotPasswordOnClick = useCallback(
		(email: string) => navigateTo(Step.FORGOT_PASSWORD, { state: { email } }),
		[navigateTo]
	);

	return (
		<StyledCard sectioned>
			<Stack tw="gap-6" vertical>
				{workspace && (
					<Stack spacing="tight" align="center" tw="w-full" justify="center">
						<Avatar url={workspace.logo} name={workspace.name} />
						<Text>
							<TextStyle variation="strong">{workspace.name}</TextStyle>
						</Text>
					</Stack>
				)}

				<Stack align="center" vertical spacing="tight" tw="w-full">
					<Heading element="h1">{t('select_auth_strategy_step.header')}</Heading>
					<Text color="subdued">{t('select_auth_strategy_step.sub_header')}</Text>
				</Stack>

				{state?.success && <Banner variation="success">{state.success}</Banner>}
				{postAuthError && <Banner variation="error">{postAuthError}</Banner>}

				{!canChangeEmail && (
					<Stack spacing="loose" align="center" justify="start" tw="w-full">
						<IconButton
							aria-label={t('password_step.back')}
							onClick={onGoBackClick}
							rounded
							source={ArrowLeft}
							title={t('password_step.back')}
							variation="secondary"
						/>
						<Text>{email}</Text>
					</Stack>
				)}

				<UsernamePasswordForm
					t={t}
					tokenUrl={requireEnv(process.env.AUTH_TOKEN_URL, 'AUTH_TOKEN_URL')}
					workspaceId={workspaceId || ''}
					onSuccess={onSuccess}
					forgotPasswordHref={forgotPasswordHref}
					forgotPasswordOnClick={forgotPasswordOnClick}
					getErrorMessage={getErrorMessage}
					defaultEmail={defaultEmail}
					showEmailField={canChangeEmail}
					renderAfterSubmit={
						canChangeEmail ? (
							<Button inverted onClick={onGoBackClick}>
								{t('general.try_different_workspace')}
							</Button>
						) : undefined
					}
				/>
			</Stack>
		</StyledCard>
	);
};

export default PasswordLoginForm;
